import React, { useState, useMemo, useCallback } from 'react';
import { Row, Col } from 'antd';
import { UpOutlined, DownOutlined } from '@ant-design/icons';
import { MyModal, MyTable, SearchInput, Spin } from 'components/Atoms';
import { FormattedMessage, useIntl } from 'react-intl';
import { useGetPreviewAudienceData, getAudienceTranslation } from 'components/Pages/Library/components/Audiences/services';
import { separateCost } from 'utilities/stringHelper';

import './styles.scss';
import { ArrowDown, ArrowUp2 } from 'components/Atoms/Icon';

const PreviewAudienceModal = ({ visible, onClose, audienceIds, filters, previewPayload, onCountTakesLonger }) => {
  const intl = useIntl();
  const [searchText, setSearchText] = useState('');
  const [sortConfig, setSortConfig] = useState({ column: null, order: null });

  // Build payload from props — supports both curated (audienceIds) and custom (previewPayload) modes
  const payload = visible ? (previewPayload || (audienceIds?.length ? { audienceIds, filters } : null)) : null;

  const handleCountTakesLonger = useCallback(() => {
    onClose?.();
    onCountTakesLonger?.();
  }, [onCountTakesLonger, onClose]);

  const { data: previewData, isLoading } = useGetPreviewAudienceData(payload, {
    onCountTakesLonger: handleCountTakesLonger,
  });

  const translateCategoryName = useCallback((categoryName, groupName, intl) => {
    if (groupName === 'Age') {
      const ageKeyMap = {
        '18-24': 'audience.demographic.Age.1824',
        '25-34': 'audience.demographic.Age.2534',
        '35-44': 'audience.demographic.Age.3544',
        '45-54': 'audience.demographic.Age.4554',
        '55-64': 'audience.demographic.Age.5564',
        '65-74': 'audience.demographic.Age.6574',
        'Over 75': 'audience.demographic.Age.Over75',
      };
      const translationKey = ageKeyMap[categoryName];
      if (translationKey) {
        return intl.formatMessage({
          id: translationKey,
          defaultMessage: categoryName,
        });
      }
    }
    if (groupName === 'Income') {
      const incomeKeyMap = {
        'Under $30K': 'audience.demographic.Income.Under30K',
        '$30K - $50K': 'audience.demographic.Income.30K50K',
        '$50K - $75K': 'audience.demographic.Income.50K75K',
        '$75K - $100K': 'audience.demographic.Income.75K100K',
        'Over $100K': 'audience.demographic.Income.Over100K',
        Unknown: 'audience.demographic.Income.Unknown',
      };
      const translationKey = incomeKeyMap[categoryName];
      if (translationKey) {
        return intl.formatMessage({
          id: translationKey,
          defaultMessage: categoryName,
        });
      }
    }
    if (groupName === 'Education') {
      const educationKeyMap = {
        'High School': 'audience.demographic.EducationLevel.HighSchoolDiploma',
        'Some College': 'audience.demographic.EducationLevel.SomeCollege',
        "Bachelor's Degree":
          'audience.demographic.EducationLevel.4YearCollegeDegree',
        'Graduate Degree':
          'audience.demographic.EducationLevel.GraduateProfessionalDegree',
        Unknown: 'audience.demographic.EducationLevel.Unknown',
      };
      const translationKey = educationKeyMap[categoryName];
      if (translationKey) {
        return intl.formatMessage({
          id: translationKey,
          defaultMessage: categoryName,
        });
      }
    }
    // Use getAudienceTranslation for other categories (Community Type, etc.)
    return getAudienceTranslation(categoryName, groupName, 'demographic', intl);
  }, []);

  const tableData = useMemo(() => {
    if (!previewData?.demographics) return [];

    const treeData = [];

    previewData.demographics.forEach((group) => {
      const hasMultipleCategories = group.categories.length > 1;
      const translatedGroupName = getAudienceTranslation(
        group.group,
        null,
        'demographic',
        intl
      );

      if (hasMultipleCategories) {
        // Create parent row with children
        const children = group.categories.map((category) => {
          const translatedCategoryName = translateCategoryName(
            category.name,
            group.group,
            intl
          );
          return {
            key: `${group.group}-${category.name}`,
            group: translatedGroupName,
            category: translatedCategoryName,
            count: category.count,
            percentage: category.percentage,
            groupName: translatedGroupName,
          };
        });

        treeData.push({
          key: `group-${group.group}`,
          group: `${translatedGroupName} (${group.categories.length})`,
          category: '',
          count: null,
          percentage: null,
          groupName: translatedGroupName,
          children: children,
        });
      } else {
        // For single category groups, show category directly (no parent)
        group.categories.forEach((category) => {
          const translatedCategoryName = translateCategoryName(
            category.name,
            group.group,
            intl
          );
          treeData.push({
            key: `${group.group}-${category.name}`,
            group: translatedGroupName,
            category: translatedCategoryName,
            count: category.count,
            percentage: category.percentage,
            groupName: translatedGroupName,
          });
        });
      }
    });

    // Filter by search text
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      const filterTree = (nodes) => {
        return nodes
          .map((node) => {
            const matches =
              node.group?.toLowerCase().includes(searchLower) ||
              node.groupName?.toLowerCase().includes(searchLower) ||
              node.category?.toLowerCase().includes(searchLower);

            let filteredChildren = [];
            if (node.children) {
              filteredChildren = filterTree(node.children);
            }

            if (matches || (filteredChildren && filteredChildren.length > 0)) {
              return {
                ...node,
                children:
                  filteredChildren.length > 0
                    ? filteredChildren
                    : node.children,
              };
            }
            return null;
          })
          .filter(Boolean);
      };

      return filterTree(treeData);
    }

    return treeData;
  }, [previewData, searchText, intl, translateCategoryName]);

  // Handle sorting
  const handleSort = useCallback(
    (column) => {
      let order = 'ascend';
      if (sortConfig.column === column && sortConfig.order === 'ascend') {
        order = 'descend';
      } else if (
        sortConfig.column === column &&
        sortConfig.order === 'descend'
      ) {
        order = null;
      }
      setSortConfig({ column: order ? column : null, order });
    },
    [sortConfig]
  );

  const sortedData = useMemo(() => {
    if (!sortConfig.column || !sortConfig.order) return tableData;

    const sortFunction = (a, b) => {
      // Handle null values (parent rows)
      if (a[sortConfig.column] === null && b[sortConfig.column] === null)
        return 0;
      if (a[sortConfig.column] === null) return 1; // Parent rows go to bottom
      if (b[sortConfig.column] === null) return -1;

      const aValue = a[sortConfig.column];
      const bValue = b[sortConfig.column];

      if (sortConfig.order === 'ascend') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    };

    // Deep clone and sort tree data
    const sorted = tableData.map((node) => {
      if (node.children && node.children.length > 0) {
        // Sort children within each parent
        const sortedChildren = [...node.children].sort(sortFunction);
        return {
          ...node,
          children: sortedChildren,
        };
      }
      return node;
    });

    // Sort top-level rows (but keep parent rows with children at their position)
    // Only sort single category rows
    return sorted.sort((a, b) => {
      // If both have children, don't sort (keep original order)
      if (a.children && b.children) return 0;
      // If one has children, keep it in place
      if (a.children) return -1;
      if (b.children) return 1;
      // Both are single rows, sort them
      return sortFunction(a, b);
    });
  }, [tableData, sortConfig]);

  const renderSortIcon = useCallback(
    (column) => {
      if (sortConfig.column !== column) {
        return (
          <span className="sort-icons">
            <UpOutlined className="sort-icon inactive" />
            <DownOutlined className="sort-icon inactive" />
          </span>
        );
      }
      return sortConfig.order === 'ascend' ? (
        <UpOutlined className="sort-icon active" />
      ) : (
        <DownOutlined className="sort-icon active" />
      );
    },
    [sortConfig]
  );

  const metrics = previewData?.count_metadata || {};

  const columns = useMemo(
    () => [
      {
        key: 'group',
        title: (
          <FormattedMessage id="campaign_create.step4.preview_audience_table_group" />
        ),
        width: 220,
        render: (text, record) => {
          if (record.children) {
            return <span className="group-header">{record.group}</span>;
          }
          return null;
        },
      },
      {
        key: 'category',
        title: (
          <FormattedMessage id="campaign_create.step4.preview_audience_table_category" />
        ),
        width: 200,
        render: (text, record) => (record.children ? '' : record.category),
      },
      {
        key: 'count',
        title: (
          <div className="sortable-header" onClick={() => handleSort('count')}>
            <FormattedMessage id="campaign_create.step4.preview_audience_table_count" />
            {renderSortIcon('count')}
          </div>
        ),
        width: 120,
        align: 'right',
        render: (text, record) =>
          record.count !== null ? separateCost(record.count) : '',
      },
      {
        key: 'percentage',
        title: (
          <div
            className="sortable-header"
            onClick={() => handleSort('percentage')}
          >
            <FormattedMessage id="campaign_create.step4.preview_audience_table_percentage" />
            {renderSortIcon('percentage')}
          </div>
        ),
        width: 120,
        align: 'right',
        render: (text, record) => {
          if (record.percentage === null) return '';
          // If percentage is already a string (e.g., "< 1%"), return as is
          if (typeof record.percentage === 'string') {
            return record.percentage.includes('%')
              ? record.percentage
              : `${record.percentage}%`;
          }
          // If percentage is a number, format it
          return `${record.percentage}%`;
        },
      },
      {
        key: 'none',
        title: (
          ''
        ),
        width: 50,
        align: 'right',
        render: () => '',
      },
      
    ],
    [handleSort, renderSortIcon]
  );

  const metricsList = [
    {
      translationId: 'campaign_create.step4.preview_audience_count',
      valueKey: 'audience_count',
      formatter: (val) => separateCost(val || 0),
    },
    {
      translationId: 'campaign_create.step4.preview_audience_percent_of_geo',
      valueKey: 'percent_of_geography',
      formatter: (val) => `${val?.toFixed(2) || '0.00'}%`,
    },
    {
      translationId: 'campaign_create.step4.preview_audience_unique_households',
      valueKey: 'unique_household_count',
      formatter: (val) => separateCost(val || 0),
    },
    {
      translationId:
        'campaign_create.step4.preview_audience_reliable_cell_phones',
      valueKey: 'unique_cellphone_count',
      formatter: (val) => separateCost(val || 0),
    },
  ];

  return (
    <MyModal
      closable
      visible={visible}
      onClose={onClose}
      width={900}
      wrapClassName="preview-audience-modal"
      title={
        <FormattedMessage
          id="campaign_create.step4.preview_audience_modal_title"
          defaultMessage="Preview Audience"
        />
      }
    >
      <div className="preview-audience-content">
        <Row gutter={[16, 16]} className="metrics-summary">
          {metricsList.map((metric, index) => (
            <Col xs={24} sm={12} md={6} key={index}>
              <div className="metric-card">
                <div className="metric-label">
                  <FormattedMessage id={metric.translationId} />
                </div>
                <div className="metric-value">
                  {isLoading ? (
                    <div className="metric-loading">
                      <Spin size="small" />
                    </div>
                    )
                    : metric.formatter(metrics[metric.valueKey])
                  }
                </div>
              </div>
            </Col>
          ))}

          <Col span={24}>
            <div className="demographics-header">
              <FormattedMessage id="campaign_create.step4.preview_audience_demographics" />
            </div>
          </Col>
          <Col span={24}>
            <div className="search-bar">
              <SearchInput
                placeholder={intl.formatMessage({
                  id: 'campaign_create.step4.preview_audience_search_placeholder',
                  defaultMessage: 'Search...',
                })}
                value={searchText}
                disabled={isLoading}
                onChange={(value) => setSearchText(value || '')}
                allowClear
              />
            </div>
          </Col>
          <Col span={24}>
            <div className="demographics-table">
              <MyTable
                data={sortedData}
                columns={columns}
                loading={isLoading}
                rowKey="key"
                scroll={{ y: 350 }}
                defaultExpandAllRows={true}
                expandable={{
                  expandRowByClick: true,
                  expandIcon: ({ expanded, onExpand, record }) => {
                    if (!record.children || record.children.length === 0) {
                      return null;
                    }
                    return (
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          onExpand(record, e);
                        }}
                        className="expand-icon-custom"
                      >{' '}
                        {expanded ? (
                          <ArrowUp2 width={18} height={18} />
                        ) : (
                          <ArrowDown width={18} height={18} />
                        )}
                      </span>
                    );
                  },
                }}
              />
            </div>
          </Col>
        </Row>
      </div>
    </MyModal>
  );
};

export default PreviewAudienceModal;
